import { useEffect, useMemo, useState } from 'react';
import Select from "react-select";
import { Modal } from "react-bootstrap";
import {
  ICustomInputRequestModels,
  ITypeCustomInput,
  ITypeCustomInputPayload,
  IvrCustomField,
} from "../../../models";
import { useForm } from "react-hook-form";
import {
  updateCustomInputFields,
  useFunctionalityDepartment,
  useFunctionalityLocation,
} from "../../../pages/hooks";
import { ExtendedCriteriaType, InputFieldType } from "../../../configs";
import { FormErrorMessage } from "../../../components";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  header?: string;
  ivrId?: string;
  customFields?: IvrCustomField[];
  onSaveHandler?: any;
  callId?: string;
  callerId?: string;
  callerCompanyId?: string;
  getCustomInputData?: ITypeCustomInput[];
  initialValues?: Record<string, any>;
  onSubmitted?: (values: Record<string, any>) => void;
  interpreterScript?: string;
  isCustomInputLoading?: boolean;
};

export function ModalIvrCustomField(props: Props) {
  const {
    show,
    handleShow,
    callId,
    callerId,
    callerCompanyId,
    getCustomInputData,
    initialValues,
    onSubmitted,
    interpreterScript,
    isCustomInputLoading = false,
  } = props;
  const [isScriptExpanded, setIsScriptExpanded] = useState(false);
  const [isPostIntakeExpanded, setIsPostIntakeExpanded] = useState(false);

  const { optionMappedLocationList } = useFunctionalityLocation({
    companyId: callerCompanyId!, shouldLocationGetAllInvoke: show
  });
  const { optionMappedDepartmentList } = useFunctionalityDepartment({
    companyId: callerCompanyId!, shouldDepartmentGetAllInvoke: show
  });

  const updatedOptionMappedLocationList = [
    { value: "", label: "Any" },
    ...optionMappedLocationList,
  ];
  const updatedOptionMappedDepartmentList = [
    { value: "", label: "Any" },
    ...optionMappedDepartmentList,
  ];

  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm<any>({
    defaultValues: initialValues ?? {},
  });
  const getFieldKey = (name: string) => name.replace(/[^a-zA-Z0-9 ]/g, "");

  const onUpdateCustomInputFieldsHandler = async (form: any) => {
    let customInputRequestModels: ICustomInputRequestModels[] = [];
    getCustomInputData?.forEach((customInput: ITypeCustomInput) => {
      if (form[getFieldKey(customInput.name)]) {
        customInputRequestModels.push({
          name: customInput.name,
          value: form[getFieldKey(customInput.name)],
          isEnabled: true,
          criteriaType: customInput.criteriaType,
        });
      }
    });

    const payload: ITypeCustomInputPayload | undefined =
      customInputRequestModels.length
        ? { customInputRequestModels: customInputRequestModels }
        : undefined;
    const didSave = await updateCustomInputFields(callId, payload, handleShow);
    if (didSave && onSubmitted) {
      onSubmitted(form);
    }
  };

  useEffect(() => {
    if (show) {
      reset(initialValues ?? {});
    }
  }, [show, reset, initialValues]);

  useEffect(() => {
    if (!show) {
      setIsScriptExpanded(false);
      setIsPostIntakeExpanded(false);
    }
  }, [show]);

  const { preIntakeScript, postIntakeScript } = useMemo(() => {
    if (!interpreterScript) {
      return { preIntakeScript: "", postIntakeScript: "" };
    }

    const markerRegex = /\[collect\s+intakes\]/i;
    const parts = interpreterScript.split(markerRegex);

    if (parts.length < 2) {
      return {
        preIntakeScript: interpreterScript.trim(),
        postIntakeScript: "",
      };
    }

    return {
      preIntakeScript: (parts[0] ?? "").trim(),
      postIntakeScript: parts.slice(1).join(" ").trim(),
    };
  }, [interpreterScript]);

  const renderScriptSection = (
    title: string,
    text: string,
    isExpanded: boolean,
    setExpanded: React.Dispatch<React.SetStateAction<boolean>>,
    options?: {
      expandable?: boolean;
      collapsedHeight?: string;
    }
  ) => {
    if (!text) return null;

    const expandable = options?.expandable ?? true;
    const collapsedHeight = options?.collapsedHeight ?? "100px";

    return (
      <div
        className="mt-3"
        style={{
          border: "1px solid var(--border)",
          borderRadius: "10px",
          backgroundColor: "var(--surface)",
          boxShadow: "0 8px 20px rgba(0, 0, 0, 0.08)",
          overflow: "hidden",
        }}
      >
        <div
          className="d-flex align-items-start justify-content-between"
          style={{
            padding: "10px 12px 8px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div className="pr-3">
            <div
              style={{
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: 1.3,
                color: "var(--text)",
              }}
            >
              {title}
            </div>
          </div>
          {expandable ? (
            <button
              type="button"
              className="btn btn-link text-muted p-0"
              onClick={() => setExpanded((prev) => !prev)}
              style={{
                fontSize: "12px",
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
                transition: "color 180ms ease-in-out",
              }}
            >
              {isExpanded ? "Show less" : "Read more"}
            </button>
          ) : null}
        </div>
        <div style={{ padding: "10px 12px 12px" }}>
          <div
            className="text-muted"
            style={{
              fontSize: "12px",
              lineHeight: 1.6,
              color: "var(--text-muted)",
            }}
          >
            <div
              style={{
                position: "relative",
                whiteSpace: "pre-wrap",
                maxHeight: expandable ? (isExpanded ? "420px" : collapsedHeight) : "none",
                opacity: expandable ? (isExpanded ? 1 : 0.9) : 1,
                overflow: "hidden",
                transition: expandable ? "max-height 320ms ease-in-out, opacity 220ms ease-in-out" : undefined,
              }}
            >
              {text}
              {expandable && !isExpanded ? (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "14px",
                    background:
                      "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.04) 35%, var(--surface) 100%)",
                    pointerEvents: "none",
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => handleShow(false)}
      centered
      backdrop="static"
    >
      <form onSubmit={handleSubmit(onUpdateCustomInputFieldsHandler)}>
        <Modal.Header closeButton>
          <Modal.Title>Custom Inputs</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="px-3 py-2">
            <div style={{ marginBottom: "25px" }}>
              {renderScriptSection(
                "Interpreter Script",
                preIntakeScript,
                isScriptExpanded,
                setIsScriptExpanded,
                {
                  expandable: false,
                }
              )}
            </div>
            <div className="row mt-1">
              <div className="col-12">
                {isCustomInputLoading ? (
                  <div
                    className="d-flex align-items-center justify-content-center text-muted"
                    style={{
                      minHeight: "175px",
                      border: "1px solid var(--border)",
                      borderRadius: "10px",
                      backgroundColor: "var(--surface)",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    loading
                  </div>
                ) : getCustomInputData ? (
                  getCustomInputData.length ? (
                    getCustomInputData?.map(
                      (customInput: ITypeCustomInput, index) => {
                        const fieldKey = getFieldKey(customInput.name);
                        const selectedValue = watch(fieldKey);
                        const options =
                          customInput.criteriaType ===
                            ExtendedCriteriaType.Department
                            ? updatedOptionMappedDepartmentList
                            : customInput.criteriaType ===
                              ExtendedCriteriaType.Location
                              ? updatedOptionMappedLocationList
                              : [];
                        const selectedOption = options.find(
                          (option) => option.label === selectedValue
                        ) ?? null;
                        return (
                        <div key={index}>
                          <div className="row mb-2">
                            <div className="col-4">
                              <label
                                htmlFor=""
                                className={`text-muted font-sz-12 ${customInput.isMandatory ? "required" : ""
                                  }`}
                              >
                                {customInput.name}
                              </label>
                            </div>
                            <div className="col-8">
                              {customInput.inputType ===
                                InputFieldType.DropDown ? (
                                <>
                                  <input
                                    className="d-none"
                                    {...register(fieldKey, {
                                      required: customInput.isMandatory,
                                    })} />
                                  <Select
                                    className={` ${errors[fieldKey]
                                      ? "field-required"
                                      : ""
                                      } `}
                                    components={{
                                      IndicatorSeparator: () => null,
                                    }}
                                    options={options}
                                    placeholder={`Select a ${customInput.name}`}
                                    value={selectedOption}
                                    onChange={(option) => {
                                      setValue(
                                        fieldKey,
                                        option?.label ?? "",
                                        { shouldValidate: true }
                                      );
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    className={`form-control ${errors[fieldKey]
                                      ? "field-required"
                                      : ""
                                      }`}
                                    {...register(fieldKey, {
                                      required: customInput.isMandatory,
                                    })}
                                    onBlur={(event) => {
                                      setValue(
                                        fieldKey,
                                        event.target.value,
                                        { shouldValidate: true }
                                      );
                                    }} />
                                </>
                              )}
                              <span>
                                {errors[fieldKey] && (
                                  <FormErrorMessage message="This field is required" />
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    )
                  ) : (
                    <div className="text-center my-4">
                      Have no custom inputs!
                    </div>
                  )
                ) : (
                  <div className="text-center my-4">Loading...</div>
                )}
              </div>
            </div>
            {renderScriptSection(
              "Interpreter Script - continued",
              postIntakeScript,
              isPostIntakeExpanded,
              setIsPostIntakeExpanded,
              {
                collapsedHeight: "50px",
              }
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-site text-uppercase"
            onClick={() => handleShow(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase btn-message-submit custom-field-submit-btn-save"
            disabled={isCustomInputLoading}
          >
            Save
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
